import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

function Groupes() {
  const navigate = useNavigate();
  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchGroupes(); }, []);

  const fetchGroupes = async () => {
    try {
      const { data } = await api.get("/groupes");
      setGroupes(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce groupe ?")) return;
    try {
      await api.delete(`/groupes/${id}`);
      fetchGroupes();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const filtered = groupes.filter((g) =>
    g.nom.toLowerCase().includes(search.toLowerCase()) ||
    g.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #00695c 0%, #00897b 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,105,92,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Gestion des Groupes
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {groupes.length} groupe{groupes.length > 1 ? "s" : ""} enregistré{groupes.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                <MDButton variant="contained" size="medium"
                  onClick={() => navigate("/groupes/nouveau")}
                  sx={{
                    background: "#fff", color: "#00695c",
                    fontWeight: 700, borderRadius: "10px",
                    "&:hover": { background: "#e0f2f1" },
                  }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Créer un groupe
                  </MDBox>
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <MDBox sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 }, mb: 3 }}>
                  <MDInput fullWidth
                    label="🔍  Rechercher un groupe..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column"
                    alignItems="center" justifyContent="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>group</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun groupe trouvé
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {search ? "Essayez une autre recherche" : "Commencez par créer un groupe"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {filtered.map((groupe) => (
                      <Grid item xs={12} sm={6} md={4} key={groupe.id}>
                        <MDBox sx={{
                          background: "#fff", borderRadius: "16px",
                          border: "1px solid #f0f0f0", padding: "20px",
                          transition: "all 0.2s", cursor: "pointer",
                          "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                        }} onClick={() => navigate(`/groupes/${groupe.id}`)}>

                          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                            <MDBox sx={{
                              width: 48, height: 48, borderRadius: "12px",
                              background: groupe.type === "permanent"
                                ? "linear-gradient(135deg, #00695c, #00897b)"
                                : "linear-gradient(135deg, #1565c0, #1976d2)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}>
                              <Icon sx={{ color: "#fff", fontSize: 24 }}>
                                {groupe.type === "permanent" ? "groups" : "group_work"}
                              </Icon>
                            </MDBox>
                            <MDBox flex={1}>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                {groupe.nom}
                              </MDTypography>
                              <MDBox sx={{
                                display: "inline-block", ml: 1,
                                padding: "1px 8px", borderRadius: "10px",
                                background: groupe.type === "permanent" ? "#e0f2f1" : "#e3f2fd",
                                color: groupe.type === "permanent" ? "#00695c" : "#1565c0",
                                fontSize: 10, fontWeight: 700,
                              }}>
                                {groupe.type === "permanent" ? "Permanent" : "Ponctuel"}
                              </MDBox>
                            </MDBox>
                          </MDBox>

                          {groupe.description && (
                            <MDTypography variant="caption" color="text" display="block" mb={2}
                              sx={{ fontStyle: "italic" }}>
                              {groupe.description}
                            </MDTypography>
                          )}

                          <MDBox display="flex" alignItems="center" mb={2}>
                            <MDBox display="flex" sx={{ "& > *": { marginLeft: "-8px" } }}>
                              {(groupe.eleves || []).slice(0, 5).map((eleve, i) => (
                                <MDBox key={eleve.id} sx={{
                                  width: 32, height: 32, borderRadius: "50%",
                                  background: ["#1565c0", "#2e7d32", "#6a1b9a", "#c62828", "#00695c"][i % 5],
                                  color: "#fff", border: "2px solid #fff",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontWeight: 700, fontSize: 11, overflow: "hidden", zIndex: 5 - i,
                                }}>
                                  {eleve.photo ? (
                                    <img src={eleve.photo} alt=""
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    `${eleve.prenom?.[0] || ""}${eleve.nom?.[0] || ""}`
                                  )}
                                </MDBox>
                              ))}
                            </MDBox>
                            <MDTypography variant="caption" color="text" sx={{ ml: 2 }}>
                              {groupe.eleves?.length || 0} élève{(groupe.eleves?.length || 0) > 1 ? "s" : ""}
                            </MDTypography>
                          </MDBox>

                          <MDBox display="flex" gap={1} mt={1}
                            onClick={(e) => e.stopPropagation()}>
                            <MDBox onClick={() => navigate(`/groupes/${groupe.id}/modifier`)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#fff8e1", color: "#f57f17",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffecb3" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>edit</Icon> Modifier
                            </MDBox>
                            <MDBox onClick={() => handleDelete(groupe.id)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffcdd2" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>cancel</Icon> Supprimer
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Groupes;